import Check from '@mui/icons-material/Check';
import Stack from '@mui/material/Stack';
import MuiSwitch, { type SwitchProps as MuiSwitchProps } from '@mui/material/Switch';
import { forwardRef } from 'react';

import { Typography } from '../Typography/Typography';

type Props = Omit<MuiSwitchProps, 'color'> & {
  label?: string;
  'data-testid'?: string;
  color?: 'primary' | 'contrast' | 'error';
};

export const Switch = forwardRef<HTMLButtonElement, Props>(({ label, color = 'primary', sx, ...props }, ref) => {
  const slotProps = {
    ...props.slotProps,
    input: {
      ...props.slotProps?.input,
      ...(label ? { 'aria-label': label } : {}),
      ...(typeof props.checked === 'boolean' ? { 'aria-checked': props.checked } : {}),
    },
  } satisfies MuiSwitchProps['slotProps'];

  const baseProps: Partial<MuiSwitchProps> = {
    checkedIcon: <Check />,
    color,
    ref: ref,
    slotProps,
  };

  const { slotProps: _slotProps, ...restProps } = props;

  if (label) {
    return (
      <Stack
        direction="row"
        sx={[
          {
            alignItems: 'center',
            flexWrap: 'nowrap',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <MuiSwitch
          {...baseProps}
          {...restProps}
          sx={{
            overflow: 'visible', // Prevents clipping of the icon
          }}
        />
        <Typography
          color={props.disabled ? 'secondary' : color === 'contrast' ? 'white' : 'default'}
          variant="body1"
          noWrap
        >
          {label}
        </Typography>
      </Stack>
    );
  }
  return (
    <MuiSwitch
      {...baseProps}
      {...restProps}
      sx={{
        ...sx,
        overflow: 'visible', // Prevents clipping of the icon
      }}
    />
  );
});

Switch.displayName = 'Switch';
